import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import {
    Collapse,
    Dialog,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@mui/material'
import cn from 'classnames'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CollectionTitle from '../asset/CollectionTitle'
import Link from '../common/util/input/Link'
import {
    formatAssetData,
    formatNumber,
    formatWAX,
} from '../helpers/FormatLinks'
import { DropPaper } from './DropPaper'
import config from '../../config.json'

export const DropTemplateDetailDialog = ({ drop, asset, setAsset }) => {
    const [collapsedImmutableData, setCollapsedImmutableData] = useState(false)
    const [collapsedMarketInfo, setCollapsedMarketInfo] = useState(false)

    const { collection } = drop

    const minted = asset?.stats ? asset.stats['numMinted'] : ''
    const supply = asset?.stats ? asset.stats['maxSupply'] : ''

    const assetData = asset?.immutableData
        ? formatAssetData(asset?.immutableData)
        : null

    const name =
        asset && asset['name']
            ? asset['name']
            : drop && drop['namePattern']
            ? drop && drop['namePattern'].replace('{num}', '')
            : ''

    const { t } = useTranslation('common')

    const searchIcon = (
        <img className={cn('w-5 h-5 flex ml-1 my-0')} src="/search.svg" />
    )
    const searchItemClassName = cn('flex justify-end cursor-pointer')

    const close = () => {
        setAsset(null)
    }

    return (
        <Dialog
            open={asset !== null}
            onClose={() => close()}
            maxWidth="md"
            fullWidth
        >
            <DropPaper title={name} noMargin>
                <div className="flex">
                    <CollectionTitle
                        collectionName={collection.collectionName}
                        displayName={collection.displayName}
                        collectionImage={collection.collectionImage}
                        collectionThumbnail={config.ipfspreviewhash
                            .replace('{collection}', collection.collectionName)
                            .replace('{hash}', collection.collectionImage)
                            .replace('{size}', '80')}
                        verified={collection.verification}
                    />
                </div>

                <Table size="small" sx={{ width: '100%' }}>
                    <TableBody>
                        {asset?.templateId && (
                            <TableRow>
                                <TableCell>Template ID:</TableCell>
                                <TableCell align="right">
                                    <Link
                                        className="text-primary hover:font-bold"
                                        href={`/template/${asset?.templateId}`}
                                    >
                                        {asset?.templateId}
                                    </Link>
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow>
                            <TableCell>{t('asset.name')}:</TableCell>
                            <TableCell align="right">{name}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>{t('asset.minted')}:</TableCell>
                            <TableCell align="right">
                                {minted}
                                {parseInt(supply) > 0 ? ` / ${supply}` : ''}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>{t('asset.schema')}:</TableCell>
                            <TableCell align="right">
                                <Link
                                    className="text-primary hover:font-bold"
                                    href={`/collection/${collection.collectionName}/${asset?.schema}`}
                                >
                                    {asset?.schema}
                                </Link>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={2}>
                                <div
                                    className="flex cursor-pointer"
                                    onClick={() => {
                                        setCollapsedImmutableData(
                                            !collapsedImmutableData,
                                        )
                                    }}
                                >
                                    {collapsedImmutableData ? (
                                        <ArrowDropDownIcon />
                                    ) : (
                                        <ArrowDropUpIcon />
                                    )}
                                    <div className="text-primary font-bold my-auto">
                                        {!collapsedImmutableData
                                            ? 'Show More '
                                            : 'Hide '}
                                        Details (Immutable Data)
                                    </div>
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                <Collapse in={collapsedImmutableData}>
                    <Table size="small">
                        <TableBody>
                            {assetData ? (
                                Object.keys(assetData).map(
                                    (key, i) =>
                                        ![
                                            'img',
                                            'video',
                                            'backimg',
                                            'pfp',
                                            '3dimg',
                                            'model',
                                            'img1',
                                            'img2',
                                            'img3',
                                            'img4',
                                        ].includes(key) && (
                                            <TableRow>
                                                <TableCell>{key}:</TableCell>
                                                <TableCell align="right">
                                                    {'' + assetData[key]}
                                                </TableCell>
                                            </TableRow>
                                        ),
                                )
                            ) : (
                                <LinearProgress />
                            )}
                        </TableBody>
                    </Table>
                </Collapse>

                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={2}>
                                <div
                                    className="flex cursor-pointer"
                                    onClick={() => {
                                        setCollapsedMarketInfo(
                                            !collapsedMarketInfo,
                                        )
                                    }}
                                >
                                    {collapsedMarketInfo ? (
                                        <ArrowDropDownIcon />
                                    ) : (
                                        <ArrowDropUpIcon />
                                    )}
                                    <div className="text-primary font-bold my-auto">
                                        {!collapsedMarketInfo
                                            ? 'Show '
                                            : 'Hide '}
                                        Market Info
                                    </div>
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                <Collapse in={collapsedMarketInfo}>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    {t('asset.average_bought')}:
                                </TableCell>
                                <TableCell align="right">
                                    {asset?.stats['averageWaxPrice']
                                        ? `${formatWAX(
                                              asset?.stats['averageWaxPrice'],
                                          )}`
                                        : null}
                                    {asset?.stats['averageUsdPrice']
                                        ? ` / $${formatNumber(
                                              asset.stats['averageUsdPrice'],
                                          )}`
                                        : '-'}
                                </TableCell>
                            </TableRow>
                            {asset?.stats['numSales'] ? (
                                <TableRow>
                                    <TableCell>
                                        {t('asset.num_sales')}
                                    </TableCell>
                                    <TableCell>
                                        {asset.stats['numSales']}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                ''
                            )}
                            {/* Floor */}
                            {asset?.stats['floorPrice'] ? (
                                <TableRow>
                                    <TableCell>
                                        {t('asset.lowest_available_price')}
                                    </TableCell>
                                    <TableCell>
                                        <Link
                                            href={`/market/?term=${escape(
                                                name,
                                            )}&collection=${
                                                collection.collectionName
                                            }&schema=${
                                                asset?.schema
                                            }&order_by=offer_asc&limit=100&template_id=${
                                                asset?.templateId
                                            }
                                            &exact_search=true`}
                                        >
                                            <div
                                                className={
                                                    'text-primary hover:underline cursor-pointer break-all'
                                                }
                                            >
                                                {formatWAX(
                                                    asset.stats['floorPrice'],
                                                )}
                                            </div>
                                        </Link>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                ''
                            )}
                            {asset?.stats['lastSold'] &&
                            asset.stats['lastSold']['priceWax'] ? (
                                // Last Sold
                                <TableRow>
                                    <TableCell>
                                        {t('asset.last_sold')}
                                    </TableCell>
                                    <TableCell>
                                        <Link
                                            href={`/sale/atomicmarket/${asset.stats['lastSold']['listingId']}`}
                                        >
                                            <div
                                                className={
                                                    'text-primary hover:underline cursor-pointer'
                                                }
                                            >
                                                {formatNumber(
                                                    asset.stats['lastSold'][
                                                        'priceWax'
                                                    ],
                                                )}{' '}
                                                WAX / $
                                                {formatNumber(
                                                    asset.stats['lastSold'][
                                                        'priceUsd'
                                                    ],
                                                )}
                                            </div>
                                        </Link>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                ''
                            )}
                        </TableBody>
                    </Table>
                </Collapse>
            </DropPaper>
        </Dialog>
    )
}
